import { useCallback, useLayoutEffect, useRef, useState } from "react";

export const useDraggablePanel = (enabled = true, preservePosition = false) => {
  const panelRef = useRef(null);
  const drag = useRef(null);
  const [position, setPosition] = useState(null);
  const [dragging, setDragging] = useState(false);
  const clamp = useCallback((left, top) => {
    const box = panelRef.current?.getBoundingClientRect();
    if (!box) return { left, top };
    const mobile = window.matchMedia('(max-width: 1023px)').matches;
    const headerBottom = mobile ? document.querySelector('.site-header')?.getBoundingClientRect().bottom || 0 : 0;
    const nav = mobile ? document.querySelector('.mobile-bottom-navigation') : null;
    const bottom = nav?.getClientRects().length ? nav.getBoundingClientRect().top : window.innerHeight;
    return { left: Math.max(8, Math.min(window.innerWidth - box.width - 8, left)), top: Math.max(headerBottom + 8, Math.min(bottom - box.height - 8, top)) };
  }, []);
  useLayoutEffect(() => {
    if (!enabled) { drag.current = null; setDragging(false); if (!preservePosition) setPosition(null); return; }
    const onResize = () => setPosition((value) => {
      if (!value) return null;
      const next = clamp(value.left, value.top);
      return next.left === value.left && next.top === value.top ? value : next;
    });
    window.addEventListener("resize", onResize);
    const observer = new ResizeObserver(onResize);
    if (panelRef.current) observer.observe(panelRef.current);
    onResize();
    return () => { observer.disconnect(); window.removeEventListener("resize", onResize); };
  }, [enabled, preservePosition, clamp]);
  const onPointerDown = (event) => {
    if (!enabled || event.button !== 0 || event.target.closest("button,select,input,a")) return;
    const box = panelRef.current.getBoundingClientRect();
    drag.current = { x: event.clientX, y: event.clientY, left: box.left, top: box.top };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };
  const onPointerMove = (event) => {
    if (!drag.current) return;
    setPosition(clamp(drag.current.left + event.clientX - drag.current.x, drag.current.top + event.clientY - drag.current.y));
  };
  const end = () => { drag.current = null; setDragging(false); };
  const onKeyDown = (event) => {
    if (event.target !== event.currentTarget || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home"].includes(event.key)) return;
    event.preventDefault();
    if (event.key === "Home") { setPosition(null); return; }
    const box = panelRef.current.getBoundingClientRect();
    const step = event.shiftKey ? 40 : 10;
    setPosition(clamp(box.left + (event.key === "ArrowRight" ? step : event.key === "ArrowLeft" ? -step : 0), box.top + (event.key === "ArrowDown" ? step : event.key === "ArrowUp" ? -step : 0)));
  };
  return { panelRef, dragging, style: position ? { left: position.left, top: position.top, right: "auto" } : undefined,
    handleProps: { onPointerDown, onPointerMove, onPointerUp: end, onPointerCancel: end, onLostPointerCapture: end, onKeyDown, tabIndex: 0 } };
};